import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthProvider';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import api from '../services/api';

interface Deposito {
  cod_dep: number;
  descricao: string;
  nome_completo: string;
}

const DepositoSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [depositos, setDepositos] = useState < Deposito[] > ([]);
  const [selectedDeposito, setSelectedDeposito] = useState < number | '' > ('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState < string | null > (null);

  useEffect(() => {
    if (!isAuthenticated) {

      navigate('/login');
      return;
    }


    const user = JSON.parse(localStorage.getItem('user') || 'null');

    // Se o usuário já tem deposito_id salvo, define ele
    if (user?.deposito_id) {
      setSelectedDeposito(user.deposito_id);

    }

    fetchDepositos(user?.deposito_id);
  }, [isAuthenticated, navigate]);


  const fetchDepositos = async (depositoIdFromUser?: number) => {
    try {
      const response = await api.get('/depositos');
      const data: Deposito[] = response.data;

      setDepositos(data);

      // Se o usuário tem deposito_id e ele existe na lista, pula direto
      if (depositoIdFromUser) {
        const match = data.find((d) => d.cod_dep === depositoIdFromUser);
        if (match) {
          localStorage.setItem('selectedDeposito', JSON.stringify(match));
          navigate('/home');
          return;
        }
      }
    } catch (err: any) {

      if (err.response?.status === 401) {

        console.error('Erro 401 ao buscar depósitos. Redirecionamento esperado.');
      }
      setError(err?.message || 'Erro ao carregar depósitos');
    } finally {
      setLoading(false);
    }
  };


  const handleSelectDeposito = (event: SelectChangeEvent<number>) => {
    setSelectedDeposito(event.target.value as number);
  };

  const handleConfirm = () => {
    if (selectedDeposito) {
      const deposito = depositos.find((d) => d.cod_dep === selectedDeposito);
      if (deposito) {
        // Salvar no localStorage
        localStorage.setItem('selectedDeposito', JSON.stringify(deposito));
        // Navegar para a home
        navigate('/home');
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #004792 0%, #38A845 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #004792 0%, #38A845 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={8} sx={{ p: 4, borderRadius: 3 }}>
          {/* Logo da Empresa */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/images/logo_nimo_energia_comprida.png"
              alt="Nimo Energia"
              sx={{ maxWidth: '80%', height: 'auto', maxHeight: 80, objectFit: 'contain' }}
            />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Selecione o Depósito
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Escolha o depósito onde você está trabalhando
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="deposito-select-label">Depósito</InputLabel>
            <Select
              labelId="deposito-select-label"
              id="deposito-select"
              value={selectedDeposito}
              label="Depósito"
              onChange={handleSelectDeposito}
            >
              {depositos.map((deposito) => (
                <MenuItem key={deposito.cod_dep} value={deposito.cod_dep}>
                  {deposito.nome_completo?.trim() ? deposito.nome_completo : deposito.descricao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleConfirm}
            disabled={!selectedDeposito}
          >
            Confirmar
          </Button>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
            Esta seleção pode ser alterada posteriormente
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default DepositoSelectionPage;
